/**
 * ODC — Email Templates CMS tab.
 * Simple list + inline editor.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, Save, Trash2, Star, StarOff, Pencil, X } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const blankDraft = {
  name: '',
  description: '',
  industry_tags: '',
  subject_template: 'Grupo BMP — {{opp_title}}',
  body_html_template: '<p>Dear {{client_name}},</p>\n<p>{{opp_summary}}</p>',
  is_default: false,
};

const OdcTemplatesTab = ({ headers }) => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/odc/templates`, { headers });
      setItems(r.data || []);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Load failed', description: e.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const handleSave = async (draft) => {
    const payload = {
      ...draft,
      industry_tags: typeof draft.industry_tags === 'string'
        ? draft.industry_tags.split(',').map((s) => s.trim()).filter(Boolean)
        : draft.industry_tags,
    };
    try {
      if (draft.id) {
        await axios.patch(`${API_URL}/api/odc/templates/${draft.id}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/api/odc/templates`, payload, { headers });
      }
      toast({ title: 'Template saved' });
      setEditing(null);
      reload();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.response?.data?.detail || e.message });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Archive this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/odc/templates/${id}`, { headers });
      toast({ title: 'Archived' });
      reload();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Delete failed', description: e.message });
    }
  };

  const openEditor = (item) => {
    if (item) {
      setEditing({ ...item, industry_tags: (item.industry_tags || []).join(', ') });
    } else {
      setEditing({ ...blankDraft });
    }
  };

  return (
    <div className="space-y-3" data-testid="odc-templates-tab">
      <div className="flex justify-end">
        <Button onClick={() => openEditor(null)} className="bg-[#0F172A] text-white" size="sm" data-testid="odc-template-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Template
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-6 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading templates…
        </div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-gray-500">No templates yet.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map((t) => (
            <Card key={t.id}>
              <CardContent className="py-3 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm font-semibold text-[#0F172A] inline-flex items-center gap-2">
                    {t.name}
                    {t.is_default && <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Star className="w-3 h-3 mr-1" /> Default</Badge>}
                  </div>
                  <div className="text-xs text-gray-500">{t.description || t.subject_template}</div>
                </div>
                <div className="flex items-center gap-1">
                  {(t.industry_tags || []).slice(0, 3).map((x) => (
                    <span key={x} className="text-[10px] uppercase bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{x}</span>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => openEditor(t)} data-testid={`tpl-edit-${t.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)} className="text-red-500 hover:bg-red-50">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
              <h3 className="text-base font-bold">{editing.id ? 'Edit Template' : 'New Template'}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs uppercase text-gray-500">Name *</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded text-sm" data-testid="tpl-name" />
              </div>
              <div>
                <label className="text-xs uppercase text-gray-500">Description</label>
                <input value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase text-gray-500">Industry tags (comma-separated)</label>
                <input value={editing.industry_tags || ''} onChange={(e) => setEditing({ ...editing, industry_tags: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase text-gray-500">Subject template *</label>
                <input value={editing.subject_template} onChange={(e) => setEditing({ ...editing, subject_template: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded text-sm" data-testid="tpl-subject" />
              </div>
              <div>
                <label className="text-xs uppercase text-gray-500">Body HTML template *</label>
                <textarea value={editing.body_html_template} onChange={(e) => setEditing({ ...editing, body_html_template: e.target.value })} rows={10} className="mt-1 w-full px-3 py-2 border rounded text-sm font-mono" data-testid="tpl-body" />
                <p className="mt-1 text-[11px] text-gray-500">
                  Tokens: <code>{`{{client_name}}`}</code>, <code>{`{{client_industry}}`}</code>, <code>{`{{opp_title}}`}</code>, <code>{`{{opp_summary}}`}</code>, <code>{`{{opp_value}}`}</code>, <code>{`{{opp_deadline}}`}</code>, <code>{`{{rationale}}`}</code>, <code>{`{{unsubscribe_url}}`}</code>
                </p>
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} />
                Set as default template
              </label>
            </div>
            <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={() => handleSave(editing)} className="bg-[#0F172A] text-white" data-testid="tpl-save">
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OdcTemplatesTab;
